import { Modal, Spin } from "antd";
import {
  Calendar,
  CheckCircle,
  Clock,
  Package,
  Settings,
  User,
} from "lucide-react";
import { FC, useEffect } from "react";
import { useTaskStore } from "./task.store";
import { useGetTask } from "~/services/task";
import dayjs from "dayjs";
import { Priority, TaskStatus } from "~/utils/constants";

const getStatusBadge = (status: TaskStatus) => {
  switch (status) {
    case TaskStatus.COMPLETED:
      return (
        <span className="px-2 py-1 rounded-full text-xs bg-green-100 text-green-800">
          Completed
        </span>
      );
    case TaskStatus.IN_PROGRESS:
      return (
        <span className="px-2 py-1 rounded-full text-xs bg-blue-100 text-blue-800">
          In Progress
        </span>
      );
    case TaskStatus.PENDING:
      return (
        <span className="px-2 py-1 rounded-full text-xs bg-yellow-100 text-yellow-800">
          Pending
        </span>
      );
    default:
      return (
        <span className="px-2 py-1 rounded-full text-xs bg-gray-100 text-gray-800">
          {status}
        </span>
      );
  }
};

const getPriorityIndicator = (priority: Priority) => {
  switch (priority) {
    case Priority.HIGH:
      return (
        <span className="px-2 py-1 rounded-full text-xs bg-red-100 text-red-800">
          High
        </span>
      );
    case Priority.MEDIUM:
      return (
        <span className="px-2 py-1 rounded-full text-xs bg-yellow-100 text-yellow-800">
          Medium
        </span>
      );
    case Priority.LOW:
      return (
        <span className="px-2 py-1 rounded-full text-xs bg-green-100 text-green-800">
          Low
        </span>
      );
    default:
      return (
        <span className="px-2 py-1 rounded-full text-xs bg-gray-100 text-gray-800">
          {priority}
        </span>
      );
  }
};

const getTaskTypeIcon = (type: string) => {
  switch (type) {
    case "maintenance":
      return <Settings size={16} />;
    case "inventory":
      return <Package size={16} />;
    case "meeting":
      return <User size={16} />;
    case "artwork":
      return <Package size={16} />;
    default:
      return <Package size={16} />;
  }
};

export const ViewTaskModal: FC = () => {
  const { viewTaskState, closeViewTaskModal } = useTaskStore();
  const { taskId, open } = viewTaskState;
  const { data: task, isLoading } = useGetTask(taskId);

  useEffect(() => {
    if (!open) {
      // Reset taskId when modal closes to avoid stale data
      closeViewTaskModal();
    }
  }, [open, closeViewTaskModal]);

  return (
    <Modal
      open={open}
      footer={null}
      onCancel={closeViewTaskModal}
      width={"70%"}
      closeIcon={<span className="text-gray-500 hover:text-gray-700">×</span>}
      title={<h1 className="text-2xl font-semibold">View Task Details</h1>}
    >
      <Spin spinning={isLoading}>
        {task && (
          <div className="space-y-6">
            <div className="bg-white rounded-lg border shadow-sm">
              <div className="p-4 border-b">
                <h2 className="font-medium">Task Details</h2>
              </div>
              <div className="p-6 space-y-4">
                <div>
                  <h3 className="text-sm font-medium text-gray-700">Title</h3>
                  <p className="text-gray-900">{task.title}</p>
                </div>
                <div>
                  <h3 className="text-sm font-medium text-gray-700">Type</h3>
                  <div className="flex items-center space-x-2">
                    {getTaskTypeIcon(task.taskType)}
                    <span className="text-gray-900">{task.taskType}</span>
                  </div>
                </div>
                <div>
                  <h3 className="text-sm font-medium text-gray-700">
                    Description
                  </h3>
                  <p className="text-gray-900">{task.description || "N/A"}</p>
                </div>
                <div className="flex space-x-6">
                  <div>
                    <h3 className="text-sm font-medium text-gray-700">
                      Due Date
                    </h3>
                    <div className="flex items-center space-x-2">
                      <Calendar size={16} />
                      <span className="text-gray-900">
                        {dayjs(task.dueDate).format("YYYY-MM-DD")}
                      </span>
                    </div>
                  </div>
                  <div>
                    <h3 className="text-sm font-medium text-gray-700">
                      Start Time
                    </h3>
                    <div className="flex items-center space-x-2">
                      <Clock size={16} />
                      <span className="text-gray-900">
                        {task.startTime || "N/A"}
                      </span>
                    </div>
                  </div>
                </div>
                <div>
                  <h3 className="text-sm font-medium text-gray-700">
                    Priority
                  </h3>
                  {getPriorityIndicator(task.priority)}
                </div>
                <div>
                  <h3 className="text-sm font-medium text-gray-700">Status</h3>
                  {getStatusBadge(task.taskStatus)}
                </div>
              </div>
            </div>

            <div className="bg-white rounded-lg border shadow-sm">
              <div className="p-4 border-b">
                <h2 className="font-medium">Assignment</h2>
              </div>
              <div className="p-6 space-y-4">
                <div>
                  <h3 className="text-sm font-medium text-gray-700">
                    Assigned To
                  </h3>
                  <div className="flex flex-wrap gap-2">
                    {task.assignees && task.assignees.length > 0 ? (
                      task.assignees.map((assignee) => (
                        <span
                          key={assignee.id}
                          className="px-2 py-1 bg-blue-100 text-blue-800 rounded"
                        >
                          {assignee.user?.username}
                        </span>
                      ))
                    ) : (
                      <span className="text-gray-500">No assignees</span>
                    )}
                  </div>
                </div>
              </div>
            </div>

            <div className="bg-white rounded-lg border shadow-sm">
              <div className="p-4 border-b">
                <h2 className="font-medium">Client & Billing</h2>
              </div>
              <div className="p-6 space-y-4">
                <div>
                  <h3 className="text-sm font-medium text-gray-700">Client</h3>
                  <p className="text-gray-900">
                    {task.client
                      ? task.client.companyName || "Unknown Client"
                      : "No client (internal task)"}
                  </p>
                </div>
                <div>
                  <h3 className="text-sm font-medium text-gray-700">
                    Billable
                  </h3>
                  <span className="text-gray-900">
                    {task.billable ? "Yes" : "No"}
                  </span>
                </div>
              </div>
            </div>

            <div className="bg-white rounded-lg border shadow-sm">
              <div className="p-4 border-b">
                <h2 className="font-medium">Related Items</h2>
              </div>
              <div className="p-6 space-y-4">
                <div>
                  <h3 className="text-sm font-medium text-gray-700">
                    Inventory Items
                  </h3>
                  {task.inventories && task.inventories.length > 0 ? (
                    <div className="flex flex-wrap gap-2">
                      {task.inventories.map((item, index) => (
                        <span
                          key={index}
                          className="px-2 py-1 bg-gray-100 rounded-lg text-sm"
                        >
                          {item.inventoryRef}: {item.title}
                        </span>
                      ))}
                    </div>
                  ) : (
                    <span className="text-gray-500">No related items</span>
                  )}
                </div>
              </div>
            </div>

            {task.notes && task.notes.length > 0 && (
              <div className="bg-white rounded-lg border shadow-sm">
                <div className="p-4 border-b">
                  <h2 className="font-medium">Notes</h2>
                </div>
                <div className="p-6 space-y-4">
                  {task.notes.map((note, index) => (
                    <div key={index} className="bg-gray-50 p-3 rounded-lg">
                      <p className="text-gray-900">{note.content}</p>
                      <p className="text-xs text-gray-500 mt-1">
                        {note.author.username} -{" "}
                        {dayjs(note.createdAt).format("YYYY-MM-DD HH:mm")}
                      </p>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {task.taskStatus === TaskStatus.COMPLETED && (
              <div className="bg-white rounded-lg border shadow-sm p-6">
                <div className="flex items-center space-x-2 text-green-600">
                  <CheckCircle size={16} />
                  <span>
                    Completed by {task.completedBy} on {task.completedDate}
                  </span>
                </div>
              </div>
            )}
          </div>
        )}
      </Spin>
    </Modal>
  );
};
